if (!customElements.get('aureme-product-points')) {
  /**
   * ポイントキャッシュの保存キーを返す
   * account ページの shared スクリプトが未読込でも、PDP 単体で同じキー名を使えるようにする
   */
  function getPointStorageKeys() {
    return (
      window.AuremeAccountShared?.storageKeys ?? {
        point: 'sanyo_app_current_point_',
        rankId: 'sanyo_app_current_rank_id_',
        rankName: 'sanyo_app_current_rank_name_',
        expire: 'sanyo_app_expire_date_',
      }
    );
  }

  /*
   * PDP のポイント表示は variant JSON と会員ランク API/cache の両方に依存する。
   * variant 切替ごとに payload を読み直し、後着した古い rank 解決で最新表示を戻さない。
   */
  customElements.define(
    'aureme-product-points',
    class AuremeProductPoints extends HTMLElement {
      variantChangeUnsubscriber = undefined;
      abortController = undefined;
      _valueEl = undefined;
      _payload = undefined;
      refreshRequestId = 0;

      connectedCallback() {
        /* 要素参照をキャッシュし、最新の JSON データは refresh 時に都度読み直す */
        this._valueEl = this.querySelector('[data-aureme-points-value]');

        this.refresh();
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (String(data?.sectionId) !== String(this.dataset.sectionId)) return;
          this.refresh();
        });
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
        this.abortController?.abort();
        this._valueEl = undefined;
        this._payload = undefined;
      }

      get valueElement() {
        return this._valueEl || null;
      }

      _parsePayload() {
        const script = this.querySelector('script[type="application/json"]');
        if (!script?.textContent) return null;

        try {
          return JSON.parse(script.textContent);
        } catch (error) {
          console.error(error);
          return null;
        }
      }

      get payload() {
        return this._payload;
      }

      async refresh() {
        const requestId = ++this.refreshRequestId;
        this._payload = this._parsePayload();
        const payload = this.payload;
        if (!payload || !this.valueElement) return;

        const rankMappings = Array.isArray(payload.rankMappings) ? payload.rankMappings : [];
        if (!rankMappings.length) {
          this.setPointText(null);
          return;
        }

        const rankId = await this.resolveRankId(payload.customerId);
        /* variant 切替中に古いランク解決が戻っても、最新 payload のポイント表示を上書きしない。 */
        if (requestId !== this.refreshRequestId) return;

        const targetEntry =
          rankMappings.find((entry) => String(entry.rank_id) === String(rankId)) ||
          rankMappings.find((entry) => String(entry.rank_id) === '5') ||
          rankMappings[0];

        if (!targetEntry) {
          this.setPointText(null);
          return;
        }

        const grantPoint = this.calculateGrantPoint(payload, targetEntry);
        this.setPointText(grantPoint);
      }

      async resolveRankId(customerId) {
        const DEFAULT_RANK_ID = '5';
        if (!customerId) return DEFAULT_RANK_ID;

        const keys = getPointStorageKeys();

        try {
          const cachedRankId = localStorage.getItem(keys.rankId + customerId);
          const cachedExpireDate = localStorage.getItem(keys.expire + customerId);
          const pointCacheTtl = window.Aureme.config.pointCacheTtl;
          const expiresAt = parseInt(cachedExpireDate, 10) + Number(pointCacheTtl);

          if (cachedRankId && cachedExpireDate && Date.now() < expiresAt) {
            return cachedRankId;
          }
        } catch (error) {
          console.error(error);
          return DEFAULT_RANK_ID;
        }

        const fetchedRankId = await this.fetchRankId(customerId);
        return fetchedRankId || DEFAULT_RANK_ID;
      }

      async fetchRankId(customerId) {
        if (!SANYO_APP_PROXY_PATH || !AUREME_STORE_DOMAIN) return null;

        this.abortController?.abort();
        this.abortController = new AbortController();
        const keys = getPointStorageKeys();

        try {
          const response = await fetch(
            `https://${AUREME_STORE_DOMAIN}${SANYO_APP_PROXY_PATH}/point`,
            {
              signal: this.abortController.signal,
            },
          );
          if (!response.ok) throw new Error('point fetch failed');

          const json = await response.json();
          if (json?.error_flg) throw new Error('point payload error');

          const point = json?.point != null ? Number(json.point) : 0;
          const rankId = json?.rankId != null ? String(json.rankId) : '';
          const rankName = json?.rankName != null ? String(json.rankName) : '';

          try {
            /* ストレージ書き込みに失敗しても、取得済みのランクID自体は返して表示を継続する */
            localStorage.setItem(keys.point + customerId, String(point));
            localStorage.setItem(keys.rankId + customerId, rankId);
            localStorage.setItem(keys.rankName + customerId, rankName);
            localStorage.setItem(keys.expire + customerId, String(Date.now()));
          } catch (storageError) {
            console.error(storageError);
          }

          return rankId || null;
        } catch (error) {
          if (error?.name !== 'AbortError') {
            console.error(error);
          }
          return null;
        }
      }

      calculateGrantPoint(payload, targetEntry) {
        const tags = Array.isArray(payload.tags) ? payload.tags : [];
        const isSaleProduct = tags.includes('セール') || tags.includes('アウトレット');

        /* セール/アウトレット商品か通常商品かでポイント計算の単価と付与率を切り替え */
        const pointPerUnit = Number(
          isSaleProduct
            ? targetEntry.sale_outlet_point_per_unit
            : targetEntry.proper_point_per_unit,
        );
        const rewardPerUnit = Number(
          isSaleProduct
            ? targetEntry.sale_outlet_reward_per_unit_points
            : targetEntry.proper_reward_per_unit_points,
        );

        if (!pointPerUnit || !rewardPerUnit) return null;

        /* 2019年10月以前に公開された商品は軽減税率(8%)を適用 */
        const publishedAt = payload.publishedAt ? new Date(payload.publishedAt) : null;
        const isValidDate = publishedAt instanceof Date && !Number.isNaN(publishedAt.valueOf());
        const isReducedTax = isValidDate && publishedAt < new Date('2019-10-01');
        const taxRate = isReducedTax ? 8 : 10;

        const priceInYen = Number(payload.price) / 100;
        if (!priceInYen) return 0;

        const priceExcludingTax = (priceInYen * 100) / (100 + taxRate);
        return Math.floor(priceExcludingTax / pointPerUnit) * rewardPerUnit;
      }

      setPointText(points) {
        if (!this.valueElement) return;

        if (points == null) {
          this.valueElement.textContent = '-';
          return;
        }

        this.valueElement.textContent = `${Number(points).toLocaleString('ja-JP')}pt`;
      }
    },
  );
}
